import { useState, useCallback } from 'react';
import { promoCodeAPI } from '@common/api/api';
import { log, LOG_LEVELS } from '../../common/config/app_logger';
import { getUser } from '../actions/user';
import { toCamelCase, withCaptcha } from '../../common/config/utils';

interface UsePromoCodeReturn {
  loading: boolean;
  success: boolean;
  error: string | null;
  creditText: string | null;
  modalText: string | null;
  applyCode: (code: string) => Promise<void>;
  clearModalText: () => void;
}

export const usePromoCode = (): UsePromoCodeReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [creditText, setCreditText] = useState<string | null>(null);
  const [modalText, setModalText] = useState<string | null>(null);

  const applyCode = useCallback(async (code: string): Promise<void> => {
    if (loading) return;
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      const res = await new Promise((resolve, reject) => {
        withCaptcha('promo', (token: string) => {
          promoCodeAPI(code, token).then(resolve).catch(reject);
        });
      }) as any;

      (window as any).store.dispatch(getUser());
      setCreditText(res.creditText);
      setModalText(res.creditText);
      setSuccess(true);
    } catch (err) {
      log(LOG_LEVELS.ERROR, 'PromoCode', 'ERROR applyCode', err);
      try {
        const json = await err.json();
        const fJson = toCamelCase(json);
        if (fJson.fullMessages && fJson.fullMessages.length > 0) {
          const [firstMessage] = fJson.fullMessages;
          setError(firstMessage);
          setModalText(firstMessage);
          setSuccess(false);
          return;
        }
        if (fJson?.errors?.promoCodeId) {
          const [firstError] = fJson.errors.promoCodeId;
          setError(firstError);
          setModalText(firstError);
          setSuccess(false);
        }
      } catch (toJsonError) {
        log(LOG_LEVELS.ERROR, 'PromoCode', 'toJsonError!', toJsonError);
      }
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const clearModalText = useCallback((): void => {
    setCreditText(null);
    setError(null);
    setModalText(null);
    setSuccess(false);
  }, []);

  return {
    loading,
    success,
    error,
    creditText,
    modalText,
    applyCode,
    clearModalText
  };
};
